import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table, Space, Tag, Select, Button, Modal, Tooltip, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs from 'dayjs';
import {
    ExclamationCircleOutlined,
    FileProtectOutlined,
    InfoCircleOutlined,
    ProjectOutlined,
    ToolOutlined,
    ScheduleOutlined
} from '@ant-design/icons';
import LoadingModal from '../../components/loadingModal/LoadingModal';
import { StatusUtils } from '../../utils/StatusUtils';
import { getConfig } from '../../config';
import { useProjectContext } from '../../context/ProjectContext';
import { useBadgeContext } from '../../context/BadgeContext';
import { TaskData } from '../../types/TaskData';
import './TaskManagement.css';
import {useLocation} from "react-router-dom";

const { Option } = Select;
const { API_BASE_URL } = getConfig();

// --- Konstanten ---
const AUTH =
    'VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU';
const DEFAULT_PRESET_FILTER = 'vorgangsfilter' as const;

type PresetKey =
    | null
    | 'kritische'
    | 'ohneAbgeschlossene'
    | 'projektleitungOffen'
    | 'montageInArbeit'
    | 'vorgangsfilter'
    | 'vorgangsfilterCritical'
    | 'montageNextWeek'
    | 'montageNextWeekPlus1To6'
    | 'montageNextWeekPlus7To12'
    | 'montageNextWeekGreater12';

const TaskManagement: React.FC = () => {
    // --- Daten & Ladezustände ---
    const [data, setData] = useState<TaskData[]>([]);
    const [filteredData, setFilteredData] = useState<TaskData[]>([]);
    const [versions, setVersions] = useState<{ version: string; versionReadable: string }[]>([]);
    const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

    const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingGantt, setIsLoadingGantt] = useState(false);

    // Tri-State: undefined = noch nicht berechnet/Loading, number = Wert, null = wirklich alle erledigt
    const [smallestActiveBuffer, setSmallestActiveBuffer] = useState<number | null | undefined>(undefined);

    // Filter
    const [presetFilter, setPresetFilter] = useState<PresetKey>(null);
    const [filters, setFilters] = useState({
        department: '',
        jobCodeDesc: '',
        projectNo: '',
        name: '',
        activityStatus: [] as string[],
        orderCardId: '',
        oglDesc: '',
        protocolDesc: ''
    });

    // Sonstiges
    const [tableKey, setTableKey] = useState(0);
    const filterInitializedRef = useRef(false);

    const { selectedProject, selectedManagerNo, setSelectedProject } = useProjectContext();
    const { refreshBadgeCounts } = useBadgeContext();

    const getFezDate = (item: TaskData) => {
        if (!item.fez) return null;
        const d = dayjs(item.fez, 'YYYY-MM-DD', true);
        return d.isValid() ? d : null;
    };

    /**
     * Berechnet Kalenderwochenbereiche relativ zur *nächsten* Kalenderwoche.
     *
     * nextWeek:       nächste Woche
     * plus1To6:       Woche (nächste +1) bis (nächste +6)
     * plus7To12:      Woche (nächste +7) bis (nächste +12)
     */
    const getMountingCalendarRanges = () => {
        const today = dayjs();
        const weekday = today.day(); // 0 = So, 1 = Mo, ...
        // Montag dieser Woche ermitteln (Mo = Wochenanfang)
        const mondayThisWeek = today
            .subtract((weekday + 6) % 7, 'day')
            .startOf('day');

        const startNextWeek = mondayThisWeek.add(1, 'week').startOf('day');
        const endNextWeek = startNextWeek.add(6, 'day').endOf('day');

        // nächste Woche +1 bis +6
        const startPlus1 = startNextWeek.add(1, 'week').startOf('day');
        const endPlus6 = startNextWeek.add(6, 'week').add(6, 'day').endOf('day');

        // nächste Woche +7 bis +12
        const startPlus7 = startNextWeek.add(7, 'week').startOf('day');
        const endPlus12 = startNextWeek.add(12, 'week').add(6, 'day').endOf('day');

        return {
            startNextWeek,
            endNextWeek,
            startPlus1,
            endPlus6,
            startPlus7,
            endPlus12
        };
    };

    const isDateInRange = (date: dayjs.Dayjs, start: dayjs.Dayjs, end: dayjs.Dayjs) => {
        const t = date.valueOf();
        return t >= start.valueOf() && t <= end.valueOf();
    };


    // --- Hilfsfunktionen ---

    const hardResetUI = () => {
        setData([]);
        setFilteredData([]);
        setVersions([]);
        setSelectedVersion(null);
        setSmallestActiveBuffer(undefined); // Loading/unknown
        filterInitializedRef.current = false;
        setPresetFilter(null);
        setFilters({
            department: '',
            jobCodeDesc: '',
            projectNo: '',
            name: '',
            activityStatus: [],
            orderCardId: '',
            oglDesc: '',
            protocolDesc: ''
        });
        setTableKey((k) => k + 1);
    };

    // 1) fetchVersions
    const fetchVersions = async (projectNo: string) => {
        const params: Record<string, any> = {
            auth: AUTH,
            projectNo,
            managerNo: selectedManagerNo ?? 'Alle', // <-- immer mitsenden
        };

        const resp = await axios.get(`${API_BASE_URL}/getNetworkPlanTaskVersions`, { params });
        const versionsData = resp.data as { version: string; versionReadable: string }[];
        if (!versionsData || versionsData.length === 0) {
            setVersions([]);
            setSelectedVersion(null);
            return null;
        }
        const sorted = versionsData.sort((a, b) => b.version.localeCompare(a.version));
        setVersions(sorted);
        setSelectedVersion(sorted[0].version);
        return sorted[0].version;
    };

    // Identifiziert eine "Kette" (Produktion => Montage => ...)
    // Du kannst die Bestandteile nach Bedarf anpassen.
    const getChainKey = (item: TaskData) => {
        return [
            item.projectNo || '',
            item.jobCodeDesc || '',
            item.protocolDesc || '',
            item.orderCardId || ''
        ].join('||');
    };


    // 2) fetchData
    const fetchData = async (projectNo: string, version?: string) => {
        const params: Record<string, any> = {
            auth: AUTH,
            projectNo,
            version: version ?? 'LATEST',          // Default, falls Projekt = "Alle"
            managerNo: selectedManagerNo ?? 'Alle' // <-- immer mitsenden
        };

        const resp = await axios.get<TaskData[]>(
            `${API_BASE_URL}/getNetworkPlanTaskData`,
            { params }
        );
        return resp.data;
    };

    const computeSmallestActiveBuffer = (rows: TaskData[]): number | null => {
        const activeBuffers = rows
            .filter(
                (item) =>
                    (item.activityStatus === 'NOT_READY' ||
                        item.activityStatus === 'READY' ||
                        item.activityStatus === 'PENDING') &&
                    item.totalBuffer !== 999
            )
            .map((item) => item.totalBuffer)
            .filter((b) => Number.isFinite(b));
        if (activeBuffers.length === 0) return null;
        return Math.min(...activeBuffers);
    };

    const getActivityIdForName = (name: string): number => {
        const item = data.find((row) => row.name === name);
        return item?.activityId || 0;
    };

    // Filterwerte aus Daten generieren (Sortierung bleibt wie bei dir)
    const getUniqueValues = (key: keyof TaskData) => {
        const unique = Array.from(new Set(data.map((item) => item[key]).filter(Boolean)));

        if (key === 'protocolDesc') {
            return unique.sort((a: any, b: any) => {
                const parse = (val: any) => (val ? val.split('/').map((v: string) => parseInt(v, 10)) : [0, 0]);
                const [aStart, aEnd] = parse(a);
                const [bStart, bEnd] = parse(b);
                return (aStart - bStart) || (aEnd - bEnd);
            });
        }

        return unique.sort((a, b) =>
            a!.toString().localeCompare(b!.toString(), 'de', { sensitivity: 'base' })
        );
    };

    const location = useLocation();
    const didInitFromQueryRef = React.useRef(false);

    useEffect(() => {
        if (didInitFromQueryRef.current) return;

        const params = new URLSearchParams(location.search);
        const projectFromUrl = params.get("project");

        if (projectFromUrl && projectFromUrl !== selectedProject) {
            // Direkter Set, ohne confirmNavigation – Seite ist frisch im neuen Tab
            setSelectedProject(projectFromUrl);
        }
        // Einmalig ausführen
        didInitFromQueryRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Produkt-Ebene (Gerüsttermin / Bezugstermin hängen hier):
        const getProductKey = (item: TaskData) =>
            [item.projectNo || '', item.ogl || ''].join('||');

    // Auftragskarten-Ebene (Produktion / Montage / Vereinbarter Montage-Termin):
        const getOrderCardKey = (item: TaskData) =>
            [item.projectNo || '', item.ogl || '', item.protocolDesc || ''].join('||');


    // Kombinierter Filter (Schnellfilter + Spaltenfilter)
    const applyCombinedFilter = (
        baseData: TaskData[],
        currentFilters: typeof filters,
        activePreset: PresetKey
    ) => {
        let presetFiltered: TaskData[];

        switch (activePreset) {
            case 'kritische':
                presetFiltered = baseData.filter(
                    (item) => item.activityDesc?.includes('Warnung') && item.activityStatus !== 'DONE'
                );
                break;
            case 'ohneAbgeschlossene':
                presetFiltered = baseData
                    .filter((item) => item.activityStatus !== 'DONE')
                break;
            case 'projektleitungOffen':
                presetFiltered = baseData
                    .filter((item) => item.department === 'Projektleiter' && item.activityStatus === 'READY')
                    .sort((a, b) => a.totalBuffer - b.totalBuffer);
                break;

            case 'montageInArbeit':
                presetFiltered = baseData.filter(
                    (item) =>
                        item.name === 'Montage' &&
                        item.activityStatus === 'PENDING' &&
                        item.fez &&
                        dayjs(item.fez).isBefore(dayjs(), 'day')
                );
                break;

            case 'vorgangsfilter': {
                const resultMap = new Map<string, TaskData>();
                const protocolMap = new Map<string, TaskData>();
                const statusPriority = { PENDING: 3, READY: 2, NOT_READY: 1, DONE: 0 } as const;

                const baseItems = baseData.filter(
                    (item) =>
                        !item.protocolDesc &&
                        !!item.jobCodeDesc &&
                        !item.name.toLowerCase().includes('reservation') &&
                        !(item.activityDesc?.includes('Gerüsttermin') || item.activityDesc?.includes('Bezugstermin'))
                );

                const groupedByProduct = new Map<string, TaskData[]>();
                baseItems.forEach((item) => {
                    const key = item.jobCodeDesc!;
                    if (!groupedByProduct.has(key)) groupedByProduct.set(key, []);
                    groupedByProduct.get(key)!.push(item);
                });

                groupedByProduct.forEach((items) => {
                    let bestItem: TaskData | undefined;
                    let bestPriority = -1;
                    for (let i = items.length - 1; i >= 0; i--) {
                        const it = items[i];
                        const priority = (statusPriority as any)[it.activityStatus] ?? -1;
                        if (priority > bestPriority) {
                            bestItem = it;
                            bestPriority = priority;
                        }
                    }
                    if (bestItem) resultMap.set(bestItem.jobCodeDesc!, bestItem);
                });

                const protocolItems = baseData.filter(
                    (item) =>
                        !!item.protocolDesc &&
                        !item.name.toLowerCase().includes('reservierung') &&
                        !(item.activityDesc?.includes('Gerüsttermin') || item.activityDesc?.includes('Bezugstermin')) &&
                        item.activityStatus !== 'DONE'
                );
                protocolItems.forEach((item) => {
                    if (!protocolMap.has(item.protocolDesc!)) {
                        protocolMap.set(item.protocolDesc!, item);
                    } else if (item.activityId < protocolMap.get(item.protocolDesc!)!.activityId) {
                        protocolMap.set(item.protocolDesc!, item);
                    }
                });

                const validProducts = new Set(
                    Array.from(protocolMap.values())
                        .map((item) => item.jobCodeDesc)
                        .filter(Boolean)
                );
                const filteredProductEntries = Array.from(resultMap.values()).filter((item) =>
                    validProducts.has(item.jobCodeDesc)
                );
                presetFiltered = filteredProductEntries
                    .concat(Array.from(protocolMap.values()))
                    .sort((a, b) => (a.jobCodeDesc || '').localeCompare(b.jobCodeDesc || ''));
                break;
            }

            case 'vorgangsfilterCritical': {
                const resultMapCritical = new Map<string, TaskData>();
                const protocolMapCritical = new Map<string, TaskData>();
                const statusPriority = { PENDING: 3, READY: 2, NOT_READY: 1, DONE: 0 } as const;

                const criticalOrderCardItems = baseData.filter(
                    (item) =>
                        !!item.orderCardId &&
                        !!item.jobCodeDesc &&
                        item.totalBuffer < 20 &&
                        item.activityStatus !== 'DONE' &&
                        !item.name.toLowerCase().includes('reservation') &&
                        !(item.activityDesc?.includes('Gerüsttermin') || item.activityDesc?.includes('Bezugstermin'))
                );
                const criticalProducts = new Set(criticalOrderCardItems.map((i) => i.jobCodeDesc!));

                const productCandidates = baseData.filter(
                    (item) =>
                        !!item.jobCodeDesc &&
                        criticalProducts.has(item.jobCodeDesc!) &&
                        !item.protocolDesc &&
                        !item.name.toLowerCase().includes('reservation') &&
                        !(item.activityDesc?.includes('Gerüsttermin') || item.activityDesc?.includes('Bezugstermin'))
                );

                const groupedByProduct = new Map<string, TaskData[]>();
                productCandidates.forEach((item) => {
                    const key = item.jobCodeDesc!;
                    if (!groupedByProduct.has(key)) groupedByProduct.set(key, []);
                    groupedByProduct.get(key)!.push(item);
                });

                groupedByProduct.forEach((items) => {
                    let bestItem: TaskData | undefined;
                    let bestPriority = -1;
                    for (let i = items.length - 1; i >= 0; i--) {
                        const it = items[i];
                        const priority = (statusPriority as any)[it.activityStatus] ?? -1;
                        if (priority > bestPriority) {
                            bestItem = it;
                            bestPriority = priority;
                        }
                    }
                    if (bestItem) resultMapCritical.set(bestItem.jobCodeDesc!, bestItem);
                });

                const protoCritical = criticalOrderCardItems.filter((item) => !!item.protocolDesc);
                protoCritical.forEach((item) => {
                    if (!protocolMapCritical.has(item.protocolDesc!)) {
                        protocolMapCritical.set(item.protocolDesc!, item);
                    } else if (item.activityId < protocolMapCritical.get(item.protocolDesc!)!.activityId) {
                        protocolMapCritical.set(item.protocolDesc!, item);
                    }
                });

                const validCriticalProducts = new Set(
                    Array.from(protocolMapCritical.values())
                        .map((item) => item.jobCodeDesc)
                        .filter(Boolean)
                );

                const filteredProductEntries = Array.from(resultMapCritical.values()).filter((item) =>
                    validCriticalProducts.has(item.jobCodeDesc)
                );
                presetFiltered = filteredProductEntries
                    .concat(Array.from(protocolMapCritical.values()))
                    .sort((a, b) => (a.jobCodeDesc || '').localeCompare(b.jobCodeDesc || ''));
                break;
            }
            case 'montageNextWeek':
            case 'montageNextWeekPlus1To6':
            case 'montageNextWeekPlus7To12':
            case 'montageNextWeekGreater12': {
                const {
                    startNextWeek,
                    endNextWeek,
                    startPlus1,
                    endPlus6,
                    startPlus7,
                    endPlus12
                } = getMountingCalendarRanges();

                const allMontageTasks = baseData.filter((item) => {
                    const name = (item.name || '').toLowerCase();
                    const isAgreedDate =
                        name === 'vereinbarter montage-termin' ||
                        name === 'vereinbarte montage-termin' ||
                        item.activityId === 2200;

                    return isAgreedDate && item.protocolDesc;
                });

                // 2) Nur solche Montage-Vorgänge behalten, deren FEZ im entsprechenden Zeitraum liegt
                const montageInWindow = allMontageTasks.filter((item) => {
                    const d = getFezDate(item);
                    if (!d) return false;

                    if (activePreset === 'montageNextWeek') {
                        return isDateInRange(d, startNextWeek, endNextWeek);
                    }
                    if (activePreset === 'montageNextWeekPlus1To6') {
                        return isDateInRange(d, startPlus1, endPlus6);
                    }
                    if (activePreset === 'montageNextWeekPlus7To12') {
                        return isDateInRange(d, startPlus7, endPlus12);
                    }
                    if (activePreset === 'montageNextWeekGreater12') {
                        return d.valueOf() > endPlus12.valueOf();
                    }
                    return false;
                });

                // 3) Schlüssel-Mengen aufbauen
                const orderCardKeys = new Set(montageInWindow.map((item) => getOrderCardKey(item)));
                const productKeys   = new Set(montageInWindow.map((item) => getProductKey(item)));

                // 4) Alle relevanten Vorgänge der Ketten anzeigen
                let tmp = baseData.filter((item) => {
                    const name = (item.name || '').toLowerCase();
                    const hasProtocol = !!item.protocolDesc;

                    const isProduction =
                        name === 'produktion' || item.activityId === 2000;
                    const isMontage =
                        name === 'montage' || item.activityId === 2100;
                    const isAgreedDate =
                        name === 'vereinbarter montage-termin' ||
                        name === 'vereinbarte montage-termin' ||
                        item.activityId === 2200;
                    const isScaffold =
                        name === 'geruesttermin' ||
                        name === 'gerüsttermin' ||
                        item.activityId === 2300;
                    const isMoveIn =
                        name === 'bezugstermin' || item.activityId === 2400;

                    // Auftragskarten-Ebene: Produktion / Montage / Vereinbarter Montage-Termin
                    if (hasProtocol) {
                        const key = getOrderCardKey(item);
                        if (!orderCardKeys.has(key)) return false;

                        const isOrderCardRelevant = isProduction || isMontage || isAgreedDate;
                        if (!isOrderCardRelevant) return false;

                        // Produktion nur, wenn nicht abgeschlossen
                        if (isProduction && item.activityStatus === 'DONE') {
                            return false;
                        }
                        return true;
                    }

                    // OGL-Ebene: Gerüsttermin / Bezugstermin
                    const prodKey = getProductKey(item);
                    if (!productKeys.has(prodKey)) return false;
                    const isProductRelevant = isScaffold || isMoveIn;
                    return isProductRelevant;
                });

                // 5) Reihenfolge pro OGL: zuerst Protokolle, dann Gerüst/Bezug
                const groupedByOgl = new Map<string, TaskData[]>();
                tmp.forEach((item) => {
                    const key = getProductKey(item);
                    if (!groupedByOgl.has(key)) groupedByOgl.set(key, []);
                    groupedByOgl.get(key)!.push(item);
                });

                const ordered: TaskData[] = [];

                Array.from(groupedByOgl.entries())
                    .sort((a, b) => {
                        const oglA = a[1][0]?.oglDesc || '';
                        const oglB = b[1][0]?.oglDesc || '';
                        return oglA.localeCompare(oglB, 'de', { sensitivity: 'base' });
                    })
                    .forEach(([_, items]) => {
                        items.sort((a, b) => {
                            const aHasProt = !!a.protocolDesc;
                            const bHasProt = !!b.protocolDesc;
                            // Protokoll-Zeilen zuerst
                            if (aHasProt !== bHasProt) return aHasProt ? -1 : 1;

                            // innerhalb der Protokoll-Zeilen nach Protokoll-Bez
                            if (a.protocolDesc !== b.protocolDesc) {
                                return (a.protocolDesc || '').localeCompare(b.protocolDesc || '');
                            }
                            // gleiche Protokoll-Bez oder beide ohne -> nach activityId
                            return (a.activityId || 0) - (b.activityId || 0);
                        });
                        ordered.push(...items);
                    });

                presetFiltered = ordered;
                break;
            }
            default:
                presetFiltered = baseData;
        }

        const finalFiltered = presetFiltered.filter((item) => {
            const {
                department,
                jobCodeDesc,
                projectNo,
                name,
                activityStatus,
                orderCardId,
                oglDesc,
                protocolDesc
            } = currentFilters;

            return (
                (!department || item.department?.toLowerCase() === department.toLowerCase()) &&
                (!projectNo || item.projectNo?.toLowerCase() === projectNo.toLowerCase()) &&
                (!jobCodeDesc || item.jobCodeDesc?.toLowerCase() === jobCodeDesc.toLowerCase()) &&
                (!protocolDesc || item.protocolDesc?.toLowerCase() === protocolDesc.toLowerCase()) &&
                (!name || item.name?.toLowerCase() === name.toLowerCase()) &&
                (!oglDesc || item.oglDesc?.toLowerCase() === oglDesc.toLowerCase()) &&
                (!orderCardId || (item.orderCardId && item.orderCardId.endsWith(orderCardId))) &&
                (activityStatus.length === 0 || activityStatus.includes(item.activityStatus))
            );
        });

        setFilteredData(finalFiltered);
    };

    const initializeAfterDataLoad = (rows: TaskData[]) => {
        // 1) kleinster aktiver Puffer
        const sab = computeSmallestActiveBuffer(rows);
        setSmallestActiveBuffer(sab);

        // 2) Schnellfilter default setzen und anwenden
        setPresetFilter(DEFAULT_PRESET_FILTER);
        applyCombinedFilter(rows, {
            department: '',
            jobCodeDesc: '',
            projectNo: '',
            name: '',
            activityStatus: [],
            orderCardId: '',
            oglDesc: '',
            protocolDesc: ''
        }, DEFAULT_PRESET_FILTER);

        // 3) Filter sind initialisiert
        filterInitializedRef.current = true;

        // 4) Tabelle „neu“ rendern
        setTableKey((k) => k + 1);
    };

    // --- Ladefluss: Projektwechsel ---
    useEffect(() => {
        const load = async () => {
            if (!selectedProject) return;
            hardResetUI();
            setIsLoadingData(true);
            try {
                const newestVersion = await fetchVersions(selectedProject);

                const versionToUse =
                    selectedProject === 'Alle'
                        ? 'LATEST' // <--- hier Default
                        : newestVersion;

                if (!versionToUse) {
                    setData([]);
                    setFilteredData([]);
                    setSmallestActiveBuffer(null);
                    return;
                }
                const rows = await fetchData(selectedProject, versionToUse);
                setData(rows);
                initializeAfterDataLoad(rows);
            } catch (e) {
                console.error('Initial load error:', e);
                setData([]);
                setFilteredData([]);
                setSmallestActiveBuffer(null);
            } finally {
                setIsLoadingData(false);
            }
        };

        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject, selectedManagerNo]);

    // --- Manuelle Versionsauswahl ---
    useEffect(() => {
        const loadByVersion = async () => {
            if (!selectedProject || selectedProject === 'Alle' || !selectedVersion) return;

            setIsLoadingData(true);
            setData([]);
            setFilteredData([]);
            setSmallestActiveBuffer(undefined);
            filterInitializedRef.current = false;

            try {
                const rows = await fetchData(selectedProject, selectedVersion);
                setData(rows);
                initializeAfterDataLoad(rows);
            } catch (e) {
                console.error('Version load error:', e);
                setData([]);
                setFilteredData([]);
                setSmallestActiveBuffer(null);
            } finally {
                setIsLoadingData(false);
            }
        };
        if (selectedVersion) loadByVersion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVersion]);


    // --- Reaktion auf Filteränderungen (nur wenn Daten & Initialisierung fertig) ---
    const handleFilterChange = (value: string | string[] | undefined, field: keyof typeof filters) => {
        const normalized =
            Array.isArray(value) ? value : (value && value.length > 0 ? [value] : undefined);
        const nextFilters = {
            ...filters,
            [field]:
                field === 'activityStatus'
                    ? (Array.isArray(value) ? (value as string[]) : [])
                    : (value && (value as string).length > 0 ? (value as string) : '')
        } as typeof filters;

        setFilters(nextFilters);
        if (filterInitializedRef.current) {
            applyCombinedFilter(data, nextFilters, presetFilter);
        }
    };

    // --- Aktionen ---
    const handleRecreateNetworkPlan = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/createOrRecreateProjectNetworkPlan`,
                { params: { auth: AUTH, projectNo: selectedProject, exportToDatabase: true } }
            );
            if (response.status === 200 && selectedProject) {
                const versionsResponse = await axios.get(
                    `${API_BASE_URL}/getNetworkPlanTaskVersions`,
                    { params: { auth: AUTH, projectNo: selectedProject } }
                );
                const versionsData = versionsResponse.data as { version: string; versionReadable: string }[];
                if (versionsData.length > 0) {
                    const sorted = versionsData.sort((a, b) => b.version.localeCompare(a.version));
                    setVersions(sorted);
                    const newest = sorted[0];
                    setSelectedVersion(newest.version); // löst useEffect aus
                    await refreshBadgeCounts();
                }
            } else {
                console.error('Failed to recreate network plan:', response.data);
            }
        } catch (error) {
            console.error('Error recreating network plan:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRecreateGantt = async () => {
        setIsLoadingGantt(true);
        try {
            const response = await axios.get(
                `${API_BASE_URL}/createOrRecreateProjectNetworkPlan`,
                {
                    params: {
                        auth: AUTH,
                        projectNo: selectedProject,
                        exportToTeamGantt: true
                    }
                }
            );
            if (response.status === 200) {
                window.open(
                    'https://app.teamgantt.com/projects/gantt2?ids=4071795&focusDate=2025-05-22',
                    '_blank'
                );
            } else {
                console.error('Failed to recreate network plan:', response.data);
            }
        } catch (error) {
            console.error('Error recreating network plan:', error);
        } finally {
            setIsLoadingGantt(false);
        }
    };

    const getDateCell = (val?: string) => {
        if (!val) return '-';
        const parts = val.split('-');
        if (parts.length !== 3) return val;
        const [y, m, d] = parts;
        return `${d}.${m}.${y}`;
    };

    // --- Spalten ---
    const columns: ColumnsType<TaskData> = [
        ...(selectedProject === 'Alle'
            ? [
                {
                    title: (
                        <div>
                            Projekt
                            <Select
                                allowClear
                                showSearch
                                value={filters.projectNo || undefined}
                                style={{ width: '100%' }}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="Filter"
                                onChange={(value) => handleFilterChange(value, 'projectNo')}
                                disabled={isLoadingData}
                            >
                                {getUniqueValues('projectNo').map((value) => (
                                    <Option key={value as string} value={value as string}>
                                        {value as string}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    ),
                    dataIndex: 'projectNo',
                    key: 'projectNo',
                    sorter: (a: { projectNo: any; }, b: { projectNo: any; }) => (a.projectNo || '').localeCompare(b.projectNo || ''),
                    width: 100
                }
            ]
            : []),
        {
            title: (
                <div>
                    Ressource
                    <Select
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
                        }
                        value={filters.department || undefined}
                        style={{ width: '100%' }}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Filter"
                        onChange={(value) => handleFilterChange(value, 'department')}
                        disabled={isLoadingData}
                    >
                        {getUniqueValues('department').map((value) => (
                            <Option key={value as string} value={value as string}>
                                {value as string}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'department',
            key: 'department',
            className: 'small-table-cell',
            width: 170,
            sorter: (a, b) => (a.department || '').localeCompare(b.department || '')
        },
        {
            title: (
                <div>
                    Produkt
                    <Select
                        onClick={(e) => e.stopPropagation()}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
                        }
                        style={{ width: '100%' }}
                        placeholder="Filter"
                        onChange={(value) => handleFilterChange(value, 'jobCodeDesc')}
                        disabled={isLoadingData}
                    >
                        {getUniqueValues('jobCodeDesc').map((value) => (
                            <Option key={value as string} value={value as string}>
                                {value as string}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'jobCodeDesc',
            key: 'jobCodeDesc',
            className: 'small-table-cell',
            sorter: (a, b) => (a.jobCodeDesc || '').localeCompare(b.jobCodeDesc || ''),
            render: (text: string) => text?.trim() || '-',
            width: 200
        },
        {
            title: (
                <div>
                    Bauabschnitt
                    <Select
                        onClick={(e) => e.stopPropagation()}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
                        }
                        style={{ width: '100%' }}
                        placeholder="Filter"
                        onChange={(value) => handleFilterChange(value, 'oglDesc')}
                        disabled={isLoadingData}
                    >
                        {getUniqueValues('oglDesc').map((value) => (
                            <Option key={value as string} value={value as string}>
                                {value as string}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'oglDesc',
            key: 'oglDesc',
            render: (text: string) => text?.trim() || '-',
            width: 120,
            sorter: (a, b) => (a.oglDesc || '').localeCompare(b.oglDesc || '')
        },
        {
            title: (
                <div>
                    Protokoll-Bez
                    <Select
                        onClick={(e) => e.stopPropagation()}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
                        }
                        style={{ width: '100%' }}
                        placeholder="Filter"
                        onChange={(value) => handleFilterChange(value, 'protocolDesc')}
                        disabled={isLoadingData}
                    >
                        {getUniqueValues('protocolDesc').map((value) => (
                            <Option key={value as string} value={value as string}>
                                {value as string}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'protocolDesc',
            key: 'protocolDesc',
            render: (text: string) => text?.trim() || '-',
            width: 360,
            sorter: (a, b) => (a.protocolDesc || '').localeCompare(b.protocolDesc || '')
        },
        {
            title: (
                <div>
                    Vorgang
                    <Select
                        onClick={(e) => e.stopPropagation()}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children?.toString().toLowerCase() ?? '').includes(input.toLowerCase())
                        }
                        style={{ width: '100%' }}
                        placeholder="Filter"
                        onChange={(value) => handleFilterChange(value, 'name')}
                        disabled={isLoadingData}
                    >
                        {getUniqueValues('name')
                            .sort((a, b) => {
                                const activityIdA = getActivityIdForName(a as string);
                                const activityIdB = getActivityIdForName(b as string);
                                return activityIdA - activityIdB;
                            })
                            .map((value) => (
                                <Option key={value as string} value={value as string}>
                                    {value as string}
                                </Option>
                            ))}
                    </Select>
                </div>
            ),
            dataIndex: 'name',
            width: 250,
            key: 'name',
            className: 'small-table-cell',
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            render: (name: string, record: TaskData) => {
                const isWarning =
                    (record.activityStatus && record.activityDesc?.includes('Warnung')) ||
                    (record.name === 'Montage' &&
                        record.activityStatus === 'PENDING' &&
                        record.fez &&
                        dayjs(record.fez).isBefore(dayjs(), 'day'));
                const showPlanningIcon = !!record.plannedOnBoardText && record.plannedOnBoardText.trim() !== '';
                const openStatus = record.activityStatus !== 'DONE' && record.activityStatus !== 'PENDING';

                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 8 }}>{name}</span>
                        {isWarning && (
                            <ExclamationCircleOutlined
                                style={{ color: 'red', fontSize: 18, marginLeft: 8, cursor: 'pointer' }}
                                title="Warnung"
                            />
                        )}
                        {showPlanningIcon && openStatus && (
                            <Tooltip title={record.plannedOnBoardText}>
                                <ScheduleOutlined style={{ color: '#d46b08', fontSize: 18 }} />
                            </Tooltip>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Start',
            dataIndex: 'faz',
            key: 'faz',
            sorter: (a, b) => (a.faz || '').localeCompare(b.faz || ''),
            render: (faz?: string) => getDateCell(faz),
            width: 120
        },
        {
            title: 'Ende',
            dataIndex: 'fez',
            key: 'fez',
            sorter: (a, b) => (a.fez || '').localeCompare(b.fez || ''),
            render: (fez?: string) => getDateCell(fez),
            width: 120
        },
        {
            title: 'Dauer (T.)',
            dataIndex: 'duration',
            key: 'duration',
            className: 'small-table-cell',
            sorter: (a, b) => (a.duration || 0) - (b.duration || 0),
            width: 100
        },
        {
            title: 'Puffer (T.)',
            dataIndex: 'totalBuffer',
            key: 'totalBuffer',
            className: 'small-table-cell',
            sorter: (a, b) => (a.totalBuffer || 0) - (b.totalBuffer || 0),
            width: 95,
            render: (totalBuffer: number) => {
                let backgroundColor;
                let displayText = totalBuffer?.toString();

                if (totalBuffer === 999) {
                    backgroundColor = 'darkgrey';
                    displayText = 'Fix';
                } else if (totalBuffer <= 20) {
                    const percentage = Math.max(0, totalBuffer) / 20;
                    const red = 255;
                    const green = Math.round(255 * percentage);
                    backgroundColor = `rgb(${red}, ${green}, 0)`;
                } else if (totalBuffer > 20 && totalBuffer <= 60) {
                    const percentage = (totalBuffer - 20) / 40;
                    const red = Math.round(255 * (1 - percentage));
                    const green = Math.round(180 + 75 * (1 - percentage));
                    backgroundColor = `rgb(${red}, ${green}, 0)`;
                } else {
                    backgroundColor = `rgb(0, 180, 0)`;
                }

                return (
                    <span
                        style={{
                            backgroundColor,
                            color: 'black',
                            padding: '4px 8px',
                            borderRadius: 4,
                            display: 'inline-block'
                        }}
                    >
            {displayText}
          </span>
                );
            }
        },
        {
            title: (
                <div>
                    Status
                    <Select
                        mode="multiple"
                        onClick={(e) => e.stopPropagation()}
                        allowClear
                        style={{ width: '100%' }}
                        placeholder="Filter"
                        onChange={(values) => handleFilterChange(values, 'activityStatus')}
                        disabled={isLoadingData}
                        value={filters.activityStatus}
                    >
                        {getUniqueValues('activityStatus').map((value) => (
                            <Option key={value as string} value={value as string}>
                                <Tag color={StatusUtils.getColor(value as 'NOT_READY' | 'READY' | 'PENDING' | 'DONE')}>
                                    {StatusUtils.getTranslatedStatus(value as 'NOT_READY' | 'READY' | 'PENDING' | 'DONE')}
                                </Tag>
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            dataIndex: 'activityStatus',
            key: 'activityStatus',
            className: 'small-table-cell',
            sorter: (a, b) => (a.activityStatus || '').localeCompare(b.activityStatus || ''),
            render: (status: 'DONE' | 'READY' | 'NOT_READY' | 'PENDING', record: TaskData) => {
                const color = StatusUtils.getColor(status);
                const translatedStatus = StatusUtils.getTranslatedStatus(status);
                const hasTableContent =
                    record.activityDesc?.includes('Bestellungen:') || record.activityDesc?.includes('<table');

                const isProduction = record.activityId === 2000 || record.name === 'Produktion';
                const isSurfaceMountable = record.mountingType === 'MOUNTABLE_SURFACE';

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag color={color}>{translatedStatus}</Tag>

                        {record.activityDesc && (
                            <Tooltip
                                title={
                                    <div style={{ maxWidth: hasTableContent ? '800px' : '300px' }}>
                                        {record.activityDesc && (
                                            <div dangerouslySetInnerHTML={{ __html: record.activityDesc }} />
                                        )}
                                        {record.name === 'Montage' &&
                                            record.activityStatus === 'PENDING' &&
                                            record.fez &&
                                            dayjs(record.fez).isBefore(dayjs(), 'day') && (
                                                <div style={{ color: 'red', marginTop: 6 }}>
                                                    <strong>Achtung:</strong> Montage nicht wieder eingeplant
                                                </div>
                                            )}
                                    </div>
                                }
                                overlayStyle={{ maxWidth: hasTableContent ? '800px' : '300px' }}
                            >
                                <InfoCircleOutlined style={{ color: '#989898', cursor: 'pointer' }} />
                            </Tooltip>
                        )}
                        {isProduction && isSurfaceMountable && (
                            <Tooltip title="Montierbar bis auf Oberfläche">
                                <ToolOutlined
                                    style={{
                                        color: '#c008d4',
                                        fontSize: 18,
                                        cursor: 'default'
                                    }}
                                />
                            </Tooltip>
                        )}
                    </div>
                );
            },
            width: 180
        }
    ];

    return (
        <div style={{ padding: 20 }}>
            {/* Blockier-Dialoge */}
            <LoadingModal open={isLoadingData} text="Daten werden geladen..." />
            <LoadingModal open={isLoading} text="Daten werden neu generiert, bitte warten..." />
            <LoadingModal open={isLoadingGantt} text="Daten werden auf der Zeitachse aufbereitet, bitte warten..." />

            {/* Kopfzeile */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                <Space>
                    <Button
                        icon={<ProjectOutlined />}
                        disabled={selectedProject === 'Alle' || !selectedProject}
                        onClick={() => {
                            Modal.confirm({
                                title: 'Bist Du sicher?',
                                icon: <ExclamationCircleOutlined />,
                                content: 'Dieser Vorgang dauert mehrere Sekunden.',
                                okText: 'Ja',
                                cancelText: 'Nein',
                                onOk: handleRecreateNetworkPlan
                            });
                        }}
                    >
                        Daten neu generieren
                    </Button>

                    <Button icon={<FileProtectOutlined />} disabled>
                        Netzplan aufbereiten
                    </Button>

                    <Button
                        onClick={() => {
                            hardResetUI();
                            // Nach Reset bei gleichem Projekt erneut laden
                            if (selectedProject) {
                                (async () => {
                                    setIsLoadingData(true);
                                    try {
                                        const newest = await fetchVersions(selectedProject);
                                        if (newest) {
                                            const rows = await fetchData(selectedProject, newest);
                                            setData(rows);
                                            initializeAfterDataLoad(rows);
                                        } else {
                                            setData([]);
                                            setFilteredData([]);
                                            setSmallestActiveBuffer(null);
                                        }
                                    } catch (e) {
                                        console.error('Reload after reset failed:', e);
                                        setData([]);
                                        setFilteredData([]);
                                        setSmallestActiveBuffer(null);
                                    } finally {
                                        setIsLoadingData(false);
                                    }
                                })();
                            }
                        }}
                    >
                        Alle Filter zurücksetzen
                    </Button>

                    <Select
                        style={{ width: 300 }}
                        disabled={versions.length === 0 || isLoadingData}
                        value={selectedVersion || undefined}
                        onChange={(value) => setSelectedVersion(value)}
                        placeholder="Version auswählen"
                    >
                        {versions.map((version) => (
                            <Select.Option key={version.version} value={version.version}>
                                {version.versionReadable}
                                {version.version === versions[0]?.version && ' (Aktuell)'}
                            </Select.Option>
                        ))}
                    </Select>

                    <Select
                        style={{ width: 350 }}
                        placeholder="Schnellfilter auswählen"
                        value={presetFilter || 'ungefiltert'}
                        disabled={isLoadingData}
                        onChange={(value) => {
                            const newPreset = (value === 'ungefiltert' ? null : (value as PresetKey));
                            setPresetFilter(newPreset);
                            if (filterInitializedRef.current) {
                                applyCombinedFilter(data, filters, newPreset);
                            }
                        }}
                    >
                        <Option value="ungefiltert">Ohne Filter</Option>
                        <Option value="ohneAbgeschlossene">Ohne Abgeschlossene</Option>
                        <Option value="kritische">Fehlerhafte Montage-Termine</Option>
                        <Option value="montageInArbeit">Montage in Arbeit ohne Neueinplanung</Option>
                        <Option value="projektleitungOffen">Vorgänge Bereit PL</Option>
                        <Option value="vorgangsfilter">Schnellübersicht Produkt/Protokoll</Option>
                        <Option value="vorgangsfilterCritical">
                            Schnellübersicht Produkt/Protokoll (&lt; 20 Tage)
                        </Option>
                        <Option value="__divider1" disabled>
                            ── Montageplanung ──
                        </Option>
                        <Option value="montageNextWeek">
                            Montage nächste Woche
                        </Option>
                        <Option value="montageNextWeekPlus1To6">
                            Montage in 2 - 6 Wochen
                        </Option>
                        <Option value="montageNextWeekPlus7To12">
                            Montage in 7 - 12 Wochen
                        </Option>
                        <Option value="montageNextWeekGreater12">
                            Montage in mehr als 12 Wochen
                        </Option>
                    </Select>
                </Space>

                <div style={{ marginLeft: 'auto', fontSize: 14 }}>
                    {smallestActiveBuffer === undefined ? null : // während Laden nichts zeigen
                        smallestActiveBuffer !== null ? (
                            <>
                                <strong>Kleinster aktiver Puffer:</strong>{' '}
                                <span
                                    style={{
                                        backgroundColor:
                                            smallestActiveBuffer <= 20
                                                ? `rgb(255, ${Math.round(255 * (smallestActiveBuffer / 20))}, 0)`
                                                : smallestActiveBuffer <= 60
                                                    ? `rgb(${Math.round(
                                                        255 * (1 - (smallestActiveBuffer - 20) / 40)
                                                    )}, ${Math.round(180 + 75 * (1 - (smallestActiveBuffer - 20) / 40))}, 0)`
                                                    : 'rgb(0, 180, 0)',
                                        color: 'black',
                                        padding: '4px 8px',
                                        borderRadius: 4,
                                        display: 'inline-block'
                                    }}
                                >
                {smallestActiveBuffer}
              </span>
                                <span style={{ paddingLeft: 3 }}>Tage</span>
                            </>
                        ) : (
                            <span
                                style={{
                                    backgroundColor: 'darkgrey',
                                    color: 'black',
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                    display: 'inline-block'
                                }}
                            >
              Alle Vorgänge abgeschlossen
            </span>
                        )}
                </div>
            </div>

            {/* Tabelle – während Laden leere Liste + Blockier-Modal (kein Flackern alter Daten) */}
            <Table
                key={tableKey}
                columns={columns}
                dataSource={isLoadingData ? [] : filteredData}
                rowKey="id"
                locale={{ emptyText: <Empty description="Keine Daten zur Anzeige vorhanden" /> }}
                className="small-table"
                pagination={{ pageSize: 100 }}
                scroll={{ y: 700 }}
                rowClassName={(record) => (record.totalBuffer === 999 ? 'grey-row' : '')}
            />
        </div>
    );
};

export default TaskManagement;